/**
 * Firestore Data Sanitizer
 * apps/gst-portal/src/utils/sanitizeFirestore.ts
 *
 * Firebase Firestore setDoc/batch.set strictly rejects `undefined` values:
 * "Unsupported field value: undefined"
 *
 * This utility deep-cleans objects prior to writing to Firestore,
 * ensuring no undefined keys or array elements are passed.
 */

export function sanitizeForFirestore<T>(data: T): T {
  if (data === undefined) {
    return undefined as any;
  }

  if (data === null || typeof data !== 'object') {
    return data;
  }

  // Preserve Date or Timestamp objects
  if (data instanceof Date || (typeof (data as any).toDate === 'function')) {
    return data;
  }

  if (Array.isArray(data)) {
    return data
      .filter((item) => item !== undefined)
      .map((item) => (typeof item === 'object' && item !== null ? sanitizeForFirestore(item) : item)) as any;
  }

  const result: Record<string, any> = {};
  for (const [key, value] of Object.entries(data)) {
    if (value !== undefined) {
      if (value !== null && typeof value === 'object') {
        result[key] = sanitizeForFirestore(value);
      } else {
        result[key] = value;
      }
    }
  }

  return result as T;
}
